import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UtilityService } from '@nttp/common-service/utility-service';
import { RegisterInforService } from '@pg-app/auth/services/register-infor.service';
import { RegisterStep1Service } from '@pg-app/auth/services/register-step-1.service';
import { map, mergeMap, take, takeUntil } from 'rxjs/operators';
import { ViewChild } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { ReplaySubject, Subject, forkJoin } from 'rxjs';
import { AfterViewInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ShareService } from '@nttp/common-service/share-service';
import { CommonDialogComponent } from '@pg-app/common/common-dialog/common-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { environment } from '@pg-env/environment';

export interface Country {
  id: string;
  countryCode: string;
  code: string;
  countryName: string;
}

@Component({
  selector: 'np-add-phone-number',
  templateUrl: './add-phone-number.component.html',
  styleUrls: ['./add-phone-number.component.scss']
})
export class AddPhoneNumberComponent implements OnInit, OnDestroy, AfterViewInit {
  addPhoneNumberForm: FormGroup;
  tokenHash: string;
  countries: any;
  loading = true;
  selectedCountry: {
    id: null;
    name: 'United Kingdom';
    countryCode: 'GB';
    code: '+44';
  };

  showResendEmailBox = false;
  resendEmailInfo: any;
  isPrivalgoMarkets = environment.isPrivalgoMarkets;
  brandName = environment.brandName;

  get phoneNumberCtrl(): FormControl {
    return this.addPhoneNumberForm.get('phoneNumber') as FormControl;
  }

  /** control for the selected country */
  public countryCtrl: FormControl = new FormControl();

  /** control for the MatSelect filter keyword */
  public countryFilterCtrl: FormControl = new FormControl();

  public filteredCountries: ReplaySubject<Country[]> = new ReplaySubject<Country[]>(1);

  @ViewChild('singleSelect', { static: true })
  singleSelect: MatSelect;
  private _onDestroy = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private utilityService: UtilityService,
    private registerInfo: RegisterInforService,
    private registerStep1Service: RegisterStep1Service,
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private shareService: ShareService,
    public dialog: MatDialog
  ) {

  }

  ngOnInit() {
    const snapshot = this.route.snapshot;
    this.tokenHash = snapshot.queryParamMap.get('token');
    this.addPhoneNumberForm = this.fb.group({
      phoneNumber: ['', Validators.required]
    });

    const confirmAccountRegistration = {
      tokenHash: this.tokenHash
    };
    const getCallingCode = this.shareService.callingCodeList$;
    const getInfomation = this.registerStep1Service.confirmAccountRegistration(confirmAccountRegistration);
    forkJoin([getCallingCode, getInfomation]).subscribe(res => {
      this.getAllData(res);
    }, err => {
      this.linkExpired(err);
    });
  }

  ngAfterViewInit() {
    this.setInitialValue();
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  private setInitialValue() {
    this.filteredCountries
      .pipe(
        take(1),
        takeUntil(this._onDestroy)
      )
      .subscribe(() => {
        this.singleSelect.compareWith = (a: Country, b: Country) => a.id === b.id;
      });
  }

  private filterCountries() {
    if (!this.countries) {
      return;
    }
    // get the search keyword
    let search = this.countryFilterCtrl.value;
    if (!search) {
      this.filteredCountries.next(this.countries.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the Countries
    this.filteredCountries.next(this.countries.filter(item => item.code.toLowerCase().indexOf(search) > -1 ||
      item.countryName.toLowerCase().indexOf(search.toLowerCase()) > -1));
  }

  getAllData(data) {
    this.countries = data[0];
    const info = data[1];
    this.registerInfo.userId = info.userId;
    this.registerInfo.tokenHash = this.tokenHash;

    if (info.phoneNumber) {
      const parsePhoneNumberRequest = {
        phoneNumber: info.phoneNumber,
        callingCodeId: null
      };
      this.utilityService.parsePhonenumber(parsePhoneNumberRequest).subscribe(res => {
        let callingCode = res.callingCode.replace('+', '');
        const defaultCode = callingCode ? callingCode : '44';
        this.setCountryCtrlAndCountryFilterCtrl(defaultCode);
    
        const phoneOriginal = callingCode ?
          info.phoneNumber.split(`+${callingCode}`)[1]
          : info.phoneNumber;
        const phone = phoneOriginal.replace(/^-/, '');
        this.addPhoneNumberForm.get('phoneNumber').setValue(phone);
        this.loading = false;
      });
    } else {
      this.setCountryCtrlAndCountryFilterCtrl('44');
      this.loading = false;
    }
  }

  onSubmit() {
    if (!this.phoneNumberCtrl.valid) {
      this.toastr.error('Please input valid phone number');
      return;
    }

    if (!this.countryCtrl.value) {
      return;
    }

    const combinePhoneNumberRequest = {
      phoneNumber: this.phoneNumberCtrl.value as string,
      callingCodeId: this.countryCtrl.value.id
    };

    this.utilityService
      .combinePhonenumber(combinePhoneNumberRequest).subscribe(res => {
        if (res.error){
          this.toastr.error(res.error);
        }
        else {
          const phoneForm = {
            phoneNumber: res.phoneNumber,
            userId: this.registerInfo.userId,
            clientId: this.registerInfo.clientId,
            tokenHash: this.registerInfo.tokenHash,
            callingCodeId: this.countryCtrl.value.id,
            customerType: this.registerInfo.customerType
          };

          this.registerStep1Service.addPhonenumber(phoneForm).subscribe(data => {
            this.registerInfo.userId = data.userId;
            this.registerInfo.tokenHash = this.tokenHash;
            this.registerInfo.customerType = data.customerType;
            this.registerInfo.parentType = data.parentType;
            this.registerInfo.portalId = data.portalId;
            this.registerInfo.email = data.email;
            this.router.navigate(['/auth/register/verify-passcode']);
          });
        }
      });
  }

  setCountryCtrlAndCountryFilterCtrl(callingCode: string) {
    const defaultCallingCode = this.countries.find(c => c.code === `+${callingCode}`);
    this.countryCtrl.setValue(defaultCallingCode);
    this.filteredCountries.next(this.countries.slice());
    this.countryFilterCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
      this.filterCountries();
    });
  }

  public linkExpired(err) {
    if (err.error) {
      const responseData = err.error.responseData;
      if (responseData && responseData.requireResend) {
        this.loading = false;
        this.showResendEmailBox = true;
        this.resendEmailInfo = responseData.resendInfo;
      }
      else {
        this.toastr.error(err.error.messages[0]);
        this.router.navigate(['/auth/login']);
      }

    } else {
      this.toastr.error('The Registration link has been used previously. Please contact your Relationship Manager for assistance.');
      this.router.navigate(['/auth/login']);
    }
  }

  resendEmail() {
    this.registerStep1Service.emailNotArrive(this.resendEmailInfo).subscribe(() => {
      this.dialog.open(CommonDialogComponent, {
        maxWidth: '42vw',
        data: {
          title: "A registration has been sent to your email. Please check the inbox to complete the registration",
          dialogType: 1
        }
      });
    });
  }
}
